import { useEffect, useState } from "react";
import { useActivityApi } from "../../hooks/useActivityApi";
import { MemberDTO, ComplianceHistoryResponseDTO, ComplianceYearDTO } from "../../services/apiClient";
import { getActivityCountByType, getComplianceActivityTypes, isDedicatedCategory } from "../../utils/complianceUtils";
import IconButton from "../common/IconButton";
import { Clock } from "lucide-react";

interface ComplianceHistoryOverviewProps {
    member: MemberDTO | null;
    onFullHistoryClick?: () => void;
}

const ComplianceHistoryOverview: React.FC<ComplianceHistoryOverviewProps> = ({ member, onFullHistoryClick }) => {

    const { getComplianceActivitiesHistory, isLoading, error } = useActivityApi();
    const [complianceHistory, setComplianceHistory] = useState<ComplianceHistoryResponseDTO | null>(null);
    const [requiredActivities, setRequiredActivities] = useState<any[]>([]);

    const fetchComplianceActivitiesHistory = async () => {
        if (!member?.memberCategoryId) return;

        try {
            // Backend now handles all business logic - just fetch the data
            const response = await getComplianceActivitiesHistory();
            setComplianceHistory(response?.data || null);
        } catch (error) {
            console.error('Error fetching Compliance Activities History', error);
        }
    };

    useEffect(() => {
        fetchComplianceActivitiesHistory();
    }, [member]);

    useEffect(() => {
        if (member) {
            setRequiredActivities(getComplianceActivityTypes(member.memberCategoryId!, member));
        }
    }, [member]);

    function HistoryColumn({ complianceYear, activityTypes }: {
        complianceYear: ComplianceYearDTO;
        activityTypes: any[];
    }) {
        return (
            <div className="flex flex-col gap-1 min-w-[120px]">
                <div className="flex gap-1">
                    {activityTypes.map((activityType, index) => {
                        const count = getActivityCountByType(complianceYear.activities || [], activityType.activityTypeId);
                        const shortType = activityType.type.includes('Hunt') ? 'HUNT' : 'SPORT';

                        // Check certificate status from backend
                        const certificateStatus = complianceYear.certificateStatus;
                        const statusValue = activityType.type.includes('Hunt')
                            ? certificateStatus?.hunter
                            : certificateStatus?.sport;

                        // If certificate did not exist for this year, do not render this activity type
                        if (!statusValue) {
                            return null;
                        }

                        const isNeeded = statusValue === 'needed';

                        if (!isNeeded) {
                            return (
                                <div key={index} className="p-4 text-center flex-1">
                                    <div className="text-xl font-bold text-green-600 whitespace-nowrap">
                                        Not Needed
                                    </div>
                                    <div className="text-xs text-green-600 whitespace-nowrap">
                                        {complianceYear.title} {shortType}
                                    </div>
                                </div>
                            );
                        }

                        // Determine status color based on compliance
                        const isCompliant = count >= activityType.requiredCount;
                        const statusColor = isCompliant ? 'text-green-600' : 'text-orange-600';

                        return (
                            <div key={index} className="rounded-lg bg-gray-50 p-4 text-center flex-1">
                                <div className={`text-xl font-bold ${statusColor}`}>
                                    {count} / {activityType.requiredCount}
                                </div>
                                <div className={`text-xs ${statusColor} whitespace-nowrap`}>
                                    {complianceYear.title} {shortType}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <>
            {!isLoading && member && (
                <>
                    {!isDedicatedCategory(member.memberCategoryId!)
                        ? null
                        : (
                            <div className="hidden md:block space-y-6">
                                {/* Compliance History Card */}
                                <div className="rounded-2xl bg-white p-4 shadow relative">
                                    <div className="mb-3 pr-12">
                                        <h3 className="text-sm font-semibold">COMPLIANCE HISTORY</h3>
                                    </div>

                                    <div className="absolute top-4 right-4">
                                        <IconButton
                                            label='Full History'
                                            color="blue"
                                            icon={<Clock />}
                                            onClick={onFullHistoryClick || (() => console.log('Full History clicked'))}
                                        />
                                    </div>

                                    <div className="flex gap-2 overflow-x-auto">
                                        {complianceHistory?.complianceYears && complianceHistory.complianceYears.length > 0 ? (
                                            complianceHistory.complianceYears.map((complianceYear, index) => (
                                                <HistoryColumn
                                                    key={index}
                                                    complianceYear={complianceYear}
                                                    activityTypes={requiredActivities}
                                                />
                                            ))
                                        ) : (
                                            <div className="text-center text-gray-500 py-9">
                                                <p className="text-sm">No compliance history available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                </>
            )}
        </>
    );
}

export default ComplianceHistoryOverview;